// Shared Material-UI (v4) theme objects consumed by react-admin's <Layout>
// and by the Login screen. These control the *visual* presentation only —
// palette, typography, spacing, shape and component styling. No application
// behaviour, field, label or workflow depends on anything in this file.

// Brand accent kept in the original navy / indigo family (main: #283593)
// so existing screens stay recognisable while looking more refined.
const brand = {
    light: '#5f5fc4',
    main: '#283593',
    dark: '#001064',
    contrastText: '#fff',
};

const fontStack = [
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'system-ui',
    '-apple-system',
    'sans-serif',
].join(',');

// Shared typography + shape used by both light and dark variants.
const typography = {
    fontFamily: fontStack,
    fontSize: 14,
    h1: { fontWeight: 600 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600, letterSpacing: 0.15 },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0.2 },
};

const shape = { borderRadius: 10 };

// react-admin reads these two values off the theme to size the left drawer
// (defaults: 240 / 55). The menu labels are all short — "Dashboard", "Donors",
// "Samples" — so 240px left a wide empty gutter on every screen while the data
// tables to the right were starved for width. 200px still fits the longest
// label ("Administration") comfortably.
const sidebar = {
    width: 200,
    closedWidth: 52,
};

// Single source of truth for "chrome" text — sidebar menu items and the
// action buttons above each page (Edit / List / Create). Material-UI renders
// these as `dense` menu items (0.875rem) and `small` buttons (0.8125rem),
// which read noticeably smaller than the 1rem body copy inside the page
// cards. Pinning them to the body size keeps the whole shell uniform.
const uiFontSize = '1rem';

// The navigation menu specifically — sidebar links, section headers and the
// horizontal top-menu bar. Slightly smaller than `uiFontSize` so the shell
// stays quiet next to page content. Deliberately applied through the
// react-admin menu classes rather than MuiMenuItem, so that dropdown options
// (SelectInput, ReferenceInput) keep the larger, more readable body size.
export const menuFontSize = '0.875rem';
export const menuIconSize = '1.25rem';
export const menuIconColumn = 32;

// Component-level overrides. These beautify every react-admin form, list,
// button and card at once without editing any resource file.
const buildOverrides = ({ dark }) => ({
    // Cards used by List / Edit / Create / Show pages.
    MuiCard: {
        root: {
            borderRadius: 14,
            boxShadow: dark
                ? '0 1px 3px rgba(0,0,0,0.5), 0 6px 20px rgba(0,0,0,0.35)'
                : '0 1px 2px rgba(16,24,64,0.06), 0 8px 24px rgba(16,24,64,0.06)',
            border: dark ? 'none' : '1px solid rgba(40,53,147,0.06)',
        },
    },
    // Buttons (Save, Create, bulk actions, pagination, etc.).
    MuiButton: {
        root: {
            borderRadius: 8,
            textTransform: 'none',
            paddingLeft: 16,
            paddingRight: 16,
        },
        contained: {
            boxShadow: 'none',
            '&:hover': {
                boxShadow: '0 4px 12px rgba(40,53,147,0.25)',
            },
        },
        // react-admin renders the page actions (Edit, List, Create, Export)
        // as size="small" buttons. Match them to body copy.
        sizeSmall: {
            fontSize: uiFontSize,
        },
        textSizeSmall: {
            fontSize: uiFontSize,
        },
        outlinedSizeSmall: {
            fontSize: uiFontSize,
        },
    },
    // Menu items. `root` covers page dropdowns (SelectInput, ReferenceInput),
    // which stay at body size. `dense` is what react-admin passes to the
    // sidebar links, so that is the lever for the navigation menu alone.
    MuiMenuItem: {
        root: {
            fontSize: uiFontSize,
            paddingLeft: 12,
            paddingRight: 12,
        },
        dense: {
            fontSize: menuFontSize,
        },
    },
    // <SubMenu> headers (Donors, Samples, Shipping, ...) render their label
    // through ListItemText's `primary` slot.
    MuiListItemText: {
        primary: {
            fontSize: menuFontSize,
        },
        secondary: {
            fontSize: menuFontSize,
        },
        dense: {
            fontSize: menuFontSize,
        },
    },
    // Narrower icon column and smaller glyphs to match the smaller labels.
    MuiListItemIcon: {
        root: {
            minWidth: menuIconColumn,
            '& svg': {
                fontSize: menuIconSize,
            },
        },
    },
    // Sidebar links (react-admin's MenuItemLink, which DashboardMenuItem also
    // uses). Scoped to this class so page dropdowns are left alone.
    RaMenuItemLink: {
        root: {
            fontSize: menuFontSize,
        },
        icon: {
            minWidth: menuIconColumn,
            '& svg': {
                fontSize: menuIconSize,
            },
        },
    },
    // Any table rendered inside a card (Datagrid, DataList) scrolls within its
    // own container instead of stretching the page sideways.
    MuiTableContainer: {
        root: {
            maxWidth: '100%',
        },
    },
    // Filled inputs — react-admin's default input variant.
    MuiFilledInput: {
        root: {
            borderRadius: 8,
            backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(40,53,147,0.04)',
            overflow: 'hidden',
            transition: 'background-color 150ms ease, box-shadow 150ms ease',
            '&:hover': {
                backgroundColor: dark ? 'rgba(255,255,255,0.10)' : 'rgba(40,53,147,0.07)',
            },
            '&$focused': {
                backgroundColor: dark ? 'rgba(255,255,255,0.12)' : 'rgba(40,53,147,0.08)',
            },
        },
    },
    // Outlined inputs (used by some inputs / the login fields when outlined).
    MuiOutlinedInput: {
        root: {
            borderRadius: 8,
        },
    },
    // Table styling for react-admin's Datagrid.
    MuiTableCell: {
        head: {
            fontWeight: 700,
            color: dark ? '#e8eaf6' : '#1a237e',
            backgroundColor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(40,53,147,0.05)',
            borderBottom: dark
                ? '1px solid rgba(255,255,255,0.12)'
                : '2px solid rgba(40,53,147,0.12)',
            whiteSpace: 'nowrap',
        },
        root: {
            borderBottom: dark
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(16,24,64,0.06)',
        },
    },
    MuiTableRow: {
        root: {
            transition: 'background-color 120ms ease',
            '&:hover': {
                backgroundColor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(40,53,147,0.05)',
            },
        },
    },
    // App bar gradient for a more finished header.
    MuiAppBar: {
        colorSecondary: {
            backgroundImage: 'linear-gradient(90deg, #283593 0%, #3949ab 100%)',
        },
        colorPrimary: {
            backgroundImage: 'linear-gradient(90deg, #283593 0%, #3949ab 100%)',
        },
    },
    // Chips (used for status / filters).
    MuiChip: {
        root: {
            borderRadius: 8,
            fontWeight: 600,
        },
    },
    // Table pagination / toolbars.
    MuiTablePagination: {
        toolbar: {
            minHeight: 52,
        },
    },
    // Tabs (tabbed forms).
    MuiTab: {
        root: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
});

export const lightTheme = {
    palette: {
        type: 'light',
        primary: { ...brand },
        // Preserve the original secondary accent used by the app bar.
        secondary: { ...brand },
        background: {
            default: '#f4f6fb',
            paper: '#ffffff',
        },
        text: {
            primary: '#1f2544',
        },
    },
    typography,
    shape,
    sidebar,
    overrides: buildOverrides({ dark: false }),
    props: {
        MuiButtonBase: {
            disableRipple: false,
        },
        MuiAppBar: {
            elevation: 0,
        },
    },
};

export const darkTheme = {
    palette: {
        type: 'dark', // Switching the dark mode on is a single property value change.
        primary: { ...brand, main: '#5f6fce' },
        secondary: { ...brand, main: '#5f6fce' },
        background: {
            default: '#171b2e',
            paper: '#1f2540',
        },
    },
    typography,
    shape,
    sidebar,
    overrides: buildOverrides({ dark: true }),
    props: {
        MuiAppBar: {
            elevation: 0,
        },
    },
};
